/* Optional campaigns wait for saved location and give ordering dialogs priority. */
(() => {
  'use strict';
  if (window.NiralaPromotion) {
    window.NiralaPromotion.init(document);
    return;
  }

  const controllers = new Map();
  const pageSeen = new Set();
  const editorSelections = new Set();
  let checkoutStarted = false;

  function rootsWithin(root) {
    const roots = [...root.querySelectorAll('[data-promotion-section]')];
    if (root.matches?.('[data-promotion-section]')) roots.unshift(root);
    return roots;
  }

  function create(root) {
    if (controllers.has(root)) return;
    const dialog = root.querySelector('[data-promotion-dialog]');
    const configNode = root.querySelector('[data-promotion-config]');
    if (!dialog || !configNode || typeof dialog.showModal !== 'function') return;
    let config;
    try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
    const key = `nirala-promotion:${config.id}:${String(config.campaign || 'promotion-1').slice(0, 120)}`;
    const abort = new AbortController();
    const { signal } = abort;
    let timer = null;
    let destroyed = false;
    let previewing = false;
    let restoreFocus = true;
    let previousFocus = null;

    function otherDialogOpen() {
      return [...document.querySelectorAll('dialog[open]')].some(item => item !== dialog);
    }

    function alreadySeen() {
      if (pageSeen.has(key)) return true;
      try {
        if (config.frequency === 'session') return sessionStorage.getItem(key) === '1';
        if (config.frequency === 'day') {
          const timestamp = Number(localStorage.getItem(key));
          return timestamp > 0 && Date.now() - timestamp < 86400000;
        }
      } catch (_) { /* In-memory frequency still prevents loops when storage is blocked. */ }
      return false;
    }

    function remember() {
      pageSeen.add(key);
      try {
        if (config.frequency === 'session') sessionStorage.setItem(key, '1');
        if (config.frequency === 'day') localStorage.setItem(key, String(Date.now()));
      } catch (_) { /* Browser storage is optional. */ }
    }

    function typing() {
      return document.activeElement?.matches?.('input, textarea, select, [contenteditable="true"]');
    }

    function available(preview = false) {
      if (destroyed || !root.isConnected || !config.hasImage || dialog.open || otherDialogOpen()) return false;
      if (document.visibilityState === 'hidden') return false;
      if (window.NiralaMenuCart?.isBusy?.() || window.NiralaCartControls?.isBusy?.()) return false;
      if (checkoutStarted || document.querySelector('[data-checkout-pending="true"], [data-ready-submit="true"], [data-quick-order-form][aria-busy="true"], [data-cart-controls][aria-busy="true"]')) return false;
      if (preview) return Boolean(config.designMode);
      if (!config.enabled || config.designMode || alreadySeen() || typing()) return false;
      if (!config.showWhenClosed && window.NiralaStoreHours?.canOrder && !window.NiralaStoreHours.canOrder()) return false;
      if (config.pageType === 'cart' || config.pageType === 'password' || config.pageType === 'checkout') return false;
      if (config.homepageOnly && config.pageType !== 'index') return false;
      if (config.requireLocation && !window.NiralaLocation?.hasSavedLocation?.()) return false;
      return true;
    }

    function clearTimer() { clearTimeout(timer); timer = null; }

    function open(preview = false) {
      clearTimer();
      if (!available(preview)) return false;
      previousFocus = document.activeElement;
      previewing = preview;
      restoreFocus = true;
      dialog.showModal();
      dialog.scrollTop = 0;
      if (!preview) remember();
      dialog.querySelector('[data-promotion-close]')?.focus({ preventScroll: true });
      return true;
    }

    function dismiss(restore = true) {
      clearTimer();
      restoreFocus = restore;
      if (dialog.open) {
        if (!previewing) remember();
        dialog.close();
      }
    }

    function schedule() {
      if (dialog.open && otherDialogOpen()) { dismiss(false); return; }
      if (!available()) { clearTimer(); return; }
      if (timer !== null) return;
      timer = setTimeout(() => { timer = null; open(); }, Math.max(0, Math.min(60, Number(config.delay) || 0)) * 1000);
    }

    root.addEventListener('click', event => {
      if (event.target.closest('[data-promotion-preview]')) open(true);
      if (event.target.closest('[data-promotion-close]')) dismiss();
      if (event.target.closest('[data-promotion-action]')) dismiss(false);
    }, { signal });
    dialog.addEventListener('cancel', event => { event.preventDefault(); dismiss(); }, { signal });
    dialog.addEventListener('close', () => {
      if (!previewing) remember();
      if (restoreFocus && !otherDialogOpen() && previousFocus?.isConnected && previousFocus !== document.body) previousFocus.focus({ preventScroll: true });
      previousFocus = null;
      previewing = false;
    }, { signal });
    dialog.addEventListener('click', event => {
      if (!config.closeOnBackdrop || event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dismiss();
    }, { signal });
    document.addEventListener('nirala:location-changed', schedule, { signal });
    document.addEventListener('nirala:cart-updated', schedule, { signal });
    document.addEventListener('nirala:hours-changed', schedule, { signal });
    document.addEventListener('nirala:menu-cart-idle', schedule, { signal });
    document.addEventListener('visibilitychange', schedule, { signal });
    document.addEventListener('focusin', () => { if (typing()) clearTimer(); }, { signal });
    document.addEventListener('focusout', () => { setTimeout(() => { if (!destroyed) schedule(); }, 0); }, { signal });
    document.addEventListener('close', schedule, { capture: true, signal });

    let observer;
    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(schedule);
      observer.observe(document.body, {
        subtree: true, attributes: true,
        attributeFilter: ['open', 'data-checkout-pending', 'data-ready-submit', 'aria-busy']
      });
    }

    const controller = {
      id: config.id,
      preview: () => open(true),
      schedule,
      suspend: () => dismiss(false),
      destroy() {
        destroyed = true;
        clearTimer();
        observer?.disconnect();
        abort.abort();
        if (dialog.open) dialog.close();
        controllers.delete(root);
      }
    };
    controllers.set(root, controller);
    schedule();
  }

  function init(root = document) {
    for (const [element, controller] of controllers) if (!element.isConnected) controller.destroy();
    rootsWithin(root).forEach(create);
  }

  document.addEventListener('submit', event => {
    if (event.submitter?.name !== 'checkout' && !event.submitter?.matches?.('[data-checkout]')) return;
    checkoutStarted = true;
    controllers.forEach(controller => controller.suspend());
  }, true);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [element, controller] of controllers) if (element === event.target || event.target.contains(element)) controller.destroy();
  });
  document.addEventListener('shopify:section:select', event => {
    rootsWithin(event.target).forEach(root => {
      const controller = controllers.get(root);
      if (controller && !editorSelections.has(controller.id)) {
        editorSelections.add(controller.id);
        controller.preview();
      }
    });
  });
  document.addEventListener('shopify:section:deselect', event => {
    rootsWithin(event.target).forEach(root => {
      const controller = controllers.get(root);
      if (controller) { editorSelections.delete(controller.id); controller.suspend(); }
    });
  });
  window.addEventListener('pageshow', () => {
    checkoutStarted = false;
    init(document);
    controllers.forEach(controller => controller.schedule());
  });
  window.NiralaPromotion = {
    init,
    preview: id => {
      const controller = [...controllers.values()].find(item => item.id === id);
      return controller?.preview() || false;
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
